import type { WorkPage, Img, Case, Module } from "./types"
// the cases from the Figma file "cases" (Nov 2026), one file each
import { mg } from "./cases/mg"
import { lotus } from "./cases/lotus"
import { klm } from "./cases/klm"
import { ns } from "./cases/ns"
import { bugaboo } from "./cases/bugaboo"
import { heineken } from "./cases/heineken"
import { deptFestival } from "./cases/dept-festival"
import { bol } from "./cases/bol"
import { feyenoord } from "./cases/feyenoord"
import { lufthansa } from "./cases/lufthansa"

// Images on framerusercontent (carried over from the Framer site).
const img = (id: string, w: number, h: number, pos?: string): Img => ({ id, w, h, pos })
/** an image served by the site (public/work/<case>/<name>-<width>.webp), in the widths made */
const own = (dir: string) => (n: string, w: number, h: number): Img => {
    const src = `/work/${dir}/${n}`
    return { id: src, src, w, h, widths: [...[800, 1600].filter((x) => x < w), w] }
}
const SMART = own("smart")
const CITY = own("mancity")
const FE = own("formula-e")
const UNITED = own("united")
const SECRID = own("secrid")
const INTERI = own("inter")

const SHARED = {
    stadium: img("G8uJgwlGSMKTAS8a505nNwpKaI.jpeg", 3840, 2160),
    phone: img("Gu972nvD17zWKN1ZSc99aNSrE.png", 886, 1304, "center top"),
    portrait: img("Eyj8C0tdGqzte19aUd5FJqwE4.png", 892, 1304, "center top"),
    portrait2: img("Gu972nvD17zWKN1ZSc99aNSrE.png", 886, 1304),
    screens: img("vpfl17pHVIDSDP9yj9okm3dJAE.jpeg", 3840, 2355),
    crowd: img("8rVEVJ3Fd2quYxR6iKvvr2otkLQ.jpeg", 3840, 2160),
    reel: "https://framerusercontent.com/assets/8bOHz5QixPuM2KpxRdPGXTNTpg.m4v",
}
const INTER =
    "INTER is rebellion and revolution. It challenges the status quo. Together we created a brand-new ecosystem for FC Internazionale Milano, ranging from web to app and campaign. Designed and developed together with Harmen Nanninga, Franklin Schamhart, Ruben van den Bussche, Leonardo Pantuso, among others."

// ---------- Sports ----------

const inter: Case = {
    name: "Inter",
    theme: {
        page: "#0b0935",
        gradient: "linear-gradient(180deg, #0b0935 0%, #0b0935 62.899%, #001ea0 100%)",
        stops: [
            [11, 9, 53, 0],
            [11, 9, 53, 0.629],
            [0, 30, 160, 1],
        ],
        fill: "#011ea1",
        line: "#011ea1",
        tone: "dark",
        title: "rgb(255, 255, 255)",
        body: "rgba(255, 255, 255, 0.6)",
    },
    modules: [
        // cover (Figma 72:6396): tag, Ronaldo photo over the card, the showreel (with sound)
        // over the stripes. Phones: tag, photo and card (the reel is further down, module 5).
        {
            type: "grid",
            columns: [
                { w: 220, cells: [{ h: 220, tag: { logo: "/work/inter/logo.svg", text: "FC INTERNATIONALE MILANO\nFOOTBALL CLUB\n26M FANS\n\nCLUB .COM AND APP" } }, { h: 626 }] },
                {
                    w: 443,
                    cells: [
                        { h: 443, image: INTERI("c1-photo", 886, 886) },
                        { h: 400, card: { title: "Elevating the nerazzurri to new heights", body: INTER } },
                    ],
                },
                {
                    w: 889,
                    cells: [
                        { h: 134 },
                        { h: 501, fit: true, image: { id: "/work/inter/c-reel", src: "/work/inter/c-reel", w: 1778, h: 1002, widths: [800, 1600, 1778] }, clip: { src: "/work/inter/c-reel", sound: true }, phone: false },
                        { h: 205, image: INTERI("c1-stripes", 1776, 410), phone: false },
                    ],
                },
            ],
        },
        { type: "split", image: SHARED.stadium, phone: SHARED.phone },
        // Figma 72:6535: Dzeko's card, the site (its screen inset with even margins, cut at
        // the bottom), the app on its panel. Phones: Dzeko, then the site.
        {
            type: "grid",
            columns: [
                { w: 446, cells: [{ h: 191 }, { h: 652, image: SHARED.portrait }] },
                { w: 666, cells: [{ h: 846, last: true, image: { ...INTERI("t-site", 1332, 1692), pos: "center top" } }] },
                { w: 443, cells: [{ h: 652, image: INTERI("t-phone", 886, 1304), phone: false }, { h: 191 }] },
            ],
        },
        { type: "framed", image: SHARED.crowd, top: SHARED.stadium },
        { type: "panel", image: SHARED.screens, clip: { src: "/work/inter/c-reel", sound: true }, poster: { id: "/work/inter/c-reel", src: "/work/inter/c-reel", w: 1778, h: 1002, widths: [800, 1600, 1778] } },
        { type: "video", video: { src: SHARED.reel }, image: SHARED.crowd },
    ],
}

// Figma "cases" > Man City, frames 8, 25, 26, 27
const manCity: Case = {
    name: "Man City",
    theme: {
        page: "#a3c3e7",
        gradient: "linear-gradient(180deg, #a3c3e7 0%, #91e7fc 100%)",
        stops: [
            [163, 195, 231, 0],
            [145, 231, 252, 1],
        ],
        fill: "#030723",
        line: "#030723",
        tone: "light",
        title: "#030723",
        body: "#030723",
    },
    modules: [
        {
            type: "grid",
            columns: [
                {
                    w: 220,
                    cells: [
                        {
                            h: 220,
                            tag: { logo: "/work/mancity/crest.png", text: "MANCHESTER CITY\nFOOTBALL CLUB\n26M FANS\n\nE-COM" },
                        },
                        { h: 626 },
                    ],
                },
                { w: 889, cells: [{ h: 846, image: CITY("1-1", 1778, 1692) }] },
                {
                    w: 443,
                    cells: [
                        {
                            h: 400,
                            card: {
                                title: "A modern\ne-commerce platform for The Citizens",
                                body: "The newly modernised Man City e-commerce platform offers many levels of flexibility, thanks to a built-in custom page designer and a multitude of configurable layout formats. Designed and developed together with Menno Dekker, Lisse Mastenbroek, Sem Bakkum, Leon van Zijl, among others.",
                            },
                        },
                        { h: 443, image: CITY("1-2", 884, 886), phone: false },
                    ],
                },
            ],
        },
        { type: "grid", columns: [{ w: 1557, cells: [{ h: 846, image: CITY("2-1", 3114, 1692) }] }] },
        {
            type: "grid",
            columns: [
                { w: 446, cells: [{ h: 652, image: { ...CITY("3-1", 892, 1304), pos: "center top" } }, { h: 191 }] },
                { w: 1107, cells: [{ h: 846, keep: true, image: CITY("3-2", 2214, 1692) }] },
            ],
        },
        { type: "grid", columns: [{ w: 1557, cells: [{ h: 846, image: CITY("4-1", 3114, 1692) }] }] },
    ],
}

// Figma "cases" > Formula E, frames 14, 29, 28
const formulaE: Case = {
    name: "Formula E",
    theme: {
        page: "#11181f",
        gradient: "linear-gradient(180deg, #11181f 0%, #5c36d8 100%)",
        stops: [
            [17, 24, 31, 0],
            [92, 54, 216, 1],
        ],
        fill: "#5c36d8",
        line: "#a08ae9",
        tone: "dark",
        title: "#ffffff",
        body: "rgba(255, 255, 255, 0.6)",
    },
    modules: [
        {
            type: "grid",
            columns: [
                {
                    w: 220,
                    cells: [
                        {
                            h: 220,
                            tag: {
                                logo: "/work/formula-e/logo.svg",
                                logoBox: [20, 23, 180, 28],
                                text: "ELECTRIC RACING SERIES\nFOOTBALL CLUB\n26M FANS\n\nAPP",
                            },
                        },
                        { h: 626 },
                    ],
                },
                {
                    w: 443,
                    cells: [
                        {
                            h: 400,
                            card: {
                                title: "The one electrified racing series",
                                body: "The Formula E app is a must-have buddy when attending a race. It can also be used to follow the race at home, but its main focus is on location, at the track.",
                            },
                        },
                        // (the Porsche car film)
                        { h: 443, image: FE("c-porsche-sq", 886, 886), clip: { src: "/work/formula-e/c-porsche-sq" }, phone: false },
                    ],
                },
                {
                    w: 888,
                    cells: [
                        { h: 616, w: 666, image: { ...FE("1-1", 1332, 1232), pos: "center top" } },
                        { h: 227, image: FE("1-3", 1776, 454), phone: false },
                    ],
                },
            ],
        },
        { type: "grid", columns: [{ w: 1557, cells: [{ h: 846, image: FE("2-1", 3114, 1692) }] }] },
        {
            type: "grid",
            columns: [
                { w: 666, cells: [{ h: 846, image: FE("3-1", 1332, 1692) }] },
                {
                    w: 888,
                    cells: [
                        { h: 227, image: FE("3-2", 1776, 454) },
                        { h: 616, image: FE("3-3", 1776, 1232) },
                    ],
                },
            ],
        },
    ],
}

export const SPORTS: WorkPage = { id: "sports", label: "Sports", path: "/sports", cases: [inter, manCity, feyenoord, formulaE] }

// ---------- Automotive ----------

// Figma "cases" > Smart, frames 10 (cover), 31, 30, 32, 33
const smart: Case = {
    name: "Smart",
    theme: {
        page: "#a5a5b8",
        gradient: "linear-gradient(#a5a5b8 0%, #dedee5 62.899%, #fff 100%)",
        stops: [
            [165, 165, 184, 0],
            [222, 222, 229, 0.629],
            [255, 255, 255, 1],
        ],
        fill: "#011ea1",
        line: "#141413",
        tone: "light",
        title: "rgb(20, 20, 19)",
        body: "rgb(109, 109, 115)",
    },
    modules: [
        {
            type: "cover-automotive",
            tag: {
                logo: "/work/smart/logo.svg",
                logoBox: [20, 21, 89, 23],
                text: "SMART CARS\nCAR BRAND\n\n.COM",
                color: "#d7e600",
                ink: "#141413",
            },
            hero: img("LuDJ5Cc0nERWGW8683mQVdfk.png", 1776, 800),
            square: img("dRtRKpamy8XorlSyElSjHBdKtM.png", 886, 886),
            wide: img("j6lrflKMVo5wjBGz6uqtdk6hDw.png", 1332, 886),
            text: { title: "Crafting bespoke virtual environments", body: INTER },
        },
        {
            type: "grid",
            columns: [
                { w: 776, cells: [{ image: SMART("2-2", 1552, 842), h: 421 }, { image: SMART("2-3", 1552, 842), h: 421 }] },
                { w: 776, cells: [{ image: SMART("2-1", 1552, 844), h: 422 }, { image: SMART("2-4", 1552, 842), h: 421 }] },
            ],
        },
        { type: "grid", columns: [{ w: 1557, cells: [{ image: SMART("c-snippet", 1920, 1042), clip: { src: "/work/smart/c-snippet" }, h: 846 }] }] },
        {
            type: "grid",
            columns: [
                { w: 776, cells: [{ image: SMART("4-1", 1552, 1692), h: 846 }] },
                { w: 776, cells: [{ image: SMART("4-2", 1552, 1080), h: 540 }, { image: SMART("4-3", 1552, 606), h: 303 }] },
            ],
        },
        {
            type: "grid",
            columns: [
                { w: 666, cells: [{ image: SMART("5-1", 1332, 1024), h: 512 }, { h: 331 }] },
                { w: 888, cells: [{ image: SMART("5-2", 1776, 1024), h: 512 }, { image: SMART("5-3", 1776, 662), h: 331 }] },
            ],
        },
    ] as Module[],
}

export const AUTOMOTIVE: WorkPage = { id: "automotive", label: "Automotive", path: "/automotive", cases: [smart, mg, lotus] }

// ---------- Travel ----------

// Figma "cases" > United, frames 20 (cover), 37, 39, 38
const united: Case = {
    name: "United",
    theme: {
        page: "#05125b",
        gradient: "linear-gradient(180deg, #05125b 0%, #0b0935 76.9%, #001ea0 100%)",
        stops: [
            [5, 18, 91, 0],
            [11, 9, 53, 0.769],
            [0, 30, 160, 1],
        ],
        fill: "#011ea1",
        line: "#011ea1",
        tone: "dark",
        title: "#ffffff",
        body: "rgba(255, 255, 255, 0.6)",
    },
    modules: [
        {
            type: "grid",
            columns: [
                {
                    w: 220,
                    cells: [
                        {
                            h: 220,
                            tag: {
                                logo: "/work/united/logo.png",
                                logoBox: [20, 21, 53, 53],
                                text: "UNITED AIRLINES\nFOOTBALL CLUB\n26M FANS\n\nDESIGN SYSTEM",
                                color: "#ffffff",
                                ink: "#000000",
                            },
                        },
                        { h: 626 },
                    ],
                },
                {
                    w: 776,
                    cells: [
                        { h: 529, image: UNITED("1-1", 1552, 1058) },
                        {
                            h: 314,
                            w: 348,
                            end: true,
                            card: { title: "Design refresh concept", body: "Concept designs for a design refresh and design system for United Airlines." },
                        },
                    ],
                },
                { w: 553, cells: [{ h: 846, image: UNITED("1-2", 1106, 1692), phone: false }] },
            ],
        },
        {
            type: "grid",
            columns: [
                { w: 446, cells: [{ h: 652, image: UNITED("2-1", 892, 1304) }, { h: 191 }] },
                { w: 1107, cells: [{ h: 846, image: UNITED("2-2", 2214, 1692) }] },
            ],
        },
        { type: "grid", columns: [{ w: 1557, cells: [{ h: 846, image: UNITED("3-1", 3114, 1692) }] }] },
        {
            type: "grid",
            columns: [
                { w: 1107, cells: [{ h: 846, image: UNITED("4-1", 2214, 1692) }] },
                { w: 446, cells: [{ h: 652, image: UNITED("4-2", 892, 1304) }, { h: 191 }] },
            ],
        },
    ],
}

export const TRAVEL: WorkPage = { id: "travel", label: "Travel", path: "/travel", cases: [lufthansa, united, klm, ns] }

// ---------- Fashion ----------

// Figma "cases" > Secrid, frames 17 (cover), 40, 42, 41, 43, 44
const secrid: Case = {
    name: "Secrid",
    theme: {
        page: "#e3e0d8",
        gradient: "linear-gradient(180deg, #e3e0d8 0%, #ffffff 100%)",
        stops: [
            [227, 224, 216, 0],
            [255, 255, 255, 1],
        ],
        fill: "#000000",
        line: "#bbac97",
        tone: "light",
        title: "#000000",
        body: "#000000",
    },
    modules: [
        {
            type: "grid",
            columns: [
                {
                    w: 220,
                    cells: [
                        {
                            h: 220,
                            tag: {
                                logo: "/work/secrid/logo.png",
                                logoBox: [20, 21, 113, 19],
                                text: "SECRID WALLETS\nWALLET BRAND\nSINCE 2008\n\nCAMPAIGN",
                                color: "#000000",
                            },
                        },
                        { h: 626 },
                    ],
                },
                { w: 666, cells: [{ h: 846, image: SECRID("1-1", 1332, 1692) }] },
                {
                    w: 665,
                    cells: [
                        { h: 443, image: SECRID("1-2", 1326, 886), phone: false },
                        {
                            h: 400,
                            w: 443,
                            card: {
                                title: "Premium collection, premium campaign",
                                body: "My role in this project was creative direction on all deliverables. Together with Secrid, we created a campaign film that shows the new premium wallets in their full glory. The 3D and editing were done by Bouke Verwijs, Leonardo Pantuso and Yeoni Park.",
                            },
                        },
                    ],
                },
            ],
        },
        { type: "grid", columns: [{ w: 1557, cells: [{ h: 846, image: SECRID("2-1", 3114, 1692) }] }] },
        {
            type: "grid",
            columns: [
                { w: 888, cells: [{ h: 846, image: SECRID("3-1", 1776, 1692) }] },
                {
                    w: 666,
                    cells: [
                        { h: 422, image: SECRID("3-2", 1332, 844) },
                        { h: 422, w: 443, image: SECRID("3-3", 886, 844) },
                    ],
                },
            ],
        },
        { type: "grid", columns: [{ w: 1557, cells: [{ h: 846, image: SECRID("4-1", 3114, 1692) }] }] },
        { type: "grid", columns: [{ w: 1557, cells: [{ h: 846, image: SECRID("c-premium", 1920, 1044), clip: { src: "/work/secrid/c-premium" } }] }] },
        { type: "grid", columns: [{ w: 1557, cells: [{ h: 846, image: SECRID("c-general", 1280, 696), clip: { src: "/work/secrid/c-general", sound: true } }] }] },
    ],
}

export const FASHION: WorkPage = { id: "fashion", label: "Fashion", path: "/fashion", cases: [secrid] }

// ---------- Lifestyle ----------
export const LIFESTYLE: WorkPage = { id: "lifestyle", label: "Lifestyle", path: "/lifestyle", cases: [bugaboo, heineken, deptFestival, bol] }

export const WORK_PAGES: Record<string, WorkPage> = { sports: SPORTS, automotive: AUTOMOTIVE, travel: TRAVEL, fashion: FASHION, lifestyle: LIFESTYLE }

/** the colours a page opens with: its first case's */
export const pageTheme = (p: WorkPage) => p.cases[0].theme
